'use client';

import { validateInventory } from 'lib/inventory/utils';
import type { Cart, CartItem, Product, ProductVariant } from 'lib/shopify/types';
import React, {
  createContext,
  use,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useOptimistic,
  useRef,
  useTransition
} from 'react';

// Types for cart actions
type UpdateType = 'plus' | 'minus' | 'delete';

type CartAction =
  | { type: 'UPDATE_ITEM'; payload: { merchandiseId: string; updateType: UpdateType } }
  | { type: 'ADD_ITEM'; payload: { variant: ProductVariant; product: Product; quantity?: number } }
  | { type: 'SET_LAST_ADDED'; payload: { merchandiseId: string } }
  | { type: 'CLEAR_LAST_ADDED' }
  | { type: 'RESET'; payload: Cart | undefined };

// Context type definition
type CartContextType = {
  cart: Cart | undefined;
  updateCartItem: (merchandiseId: string, updateType: UpdateType) => void;
  addCartItem: (variant: ProductVariant, product: Product, quantity?: number) => void;
  lastAddedItem: string | null;
  clearLastAddedItem: () => void;
  // Cart modal state management
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
};

// Create the context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Utility functions for price calculations
function calculateItemCost(quantity: number, price: string): string {
  return (Number(price) * quantity).toString();
}

// Update cart item based on action type
function updateCartItem(item: CartItem, updateType: UpdateType): CartItem | null {
  if (updateType === 'delete') return null;

  const newQuantity = updateType === 'plus' ? item.quantity + 1 : item.quantity - 1;
  if (newQuantity <= 0) return null;

  const singleItemAmount = Number(item.cost.totalAmount.amount) / item.quantity;
  const newTotalAmount = calculateItemCost(newQuantity, singleItemAmount.toString());

  return {
    ...item,
    quantity: newQuantity,
    cost: {
      ...item.cost,
      totalAmount: {
        ...item.cost.totalAmount,
        amount: newTotalAmount
      }
    }
  };
}

// Create or update cart item
function createOrUpdateCartItem(
  existingItem: CartItem | undefined,
  variant: ProductVariant,
  product: Product,
  quantity?: number
): CartItem {
  const newQuantity = quantity || 1;
  const baseQuantity = existingItem?.quantity || 0;
  const finalQuantity = baseQuantity + newQuantity;
  const totalAmount = calculateItemCost(finalQuantity, variant.price.amount);

  return {
    id: existingItem?.id,
    quantity: finalQuantity,
    cost: {
      totalAmount: {
        amount: totalAmount,
        currencyCode: variant.price.currencyCode
      }
    },
    merchandise: {
      id: variant.id,
      title: variant.title,
      selectedOptions: variant.selectedOptions,
      product: {
        id: product.id,
        handle: product.handle,
        title: product.title,
        featuredImage: product.featuredImage || null
      }
    }
  };
}

// Update cart totals based on line items
function updateCartTotals(lines: CartItem[]): Pick<Cart, 'totalQuantity' | 'cost'> {
  const totalQuantity = lines.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = lines.reduce((sum, item) => sum + Number(item.cost.totalAmount.amount), 0);
  const currencyCode = lines[0]?.cost.totalAmount.currencyCode ?? 'USD';

  return {
    totalQuantity,
    cost: {
      subtotalAmount: { amount: totalAmount.toString(), currencyCode },
      totalAmount: { amount: totalAmount.toString(), currencyCode },
      totalTaxAmount: { amount: '0', currencyCode }
    }
  };
}

// Create an empty cart
function createEmptyCart(): Cart {
  return {
    id: undefined,
    checkoutUrl: '',
    totalQuantity: 0,
    lines: [],
    cost: {
      subtotalAmount: { amount: '0', currencyCode: 'USD' },
      totalAmount: { amount: '0', currencyCode: 'USD' },
      totalTaxAmount: { amount: '0', currencyCode: 'USD' }
    }
  };
}

// Cart reducer function for state updates
function cartReducer(state: Cart | undefined, action: CartAction): Cart {
  const currentCart = state || createEmptyCart();

  switch (action.type) {
    case 'RESET': {
      // Reset to the server state (after revalidation)
      return action.payload || createEmptyCart();
    }

    case 'UPDATE_ITEM': {
      const { merchandiseId, updateType } = action.payload;
      const updatedLines = currentCart.lines
        .map((item) =>
          item.merchandise.id === merchandiseId ? updateCartItem(item, updateType) : item
        )
        .filter(Boolean) as CartItem[];

      if (updatedLines.length === 0) {
        return {
          ...currentCart,
          lines: [],
          totalQuantity: 0,
          cost: {
            ...currentCart.cost,
            totalAmount: { ...currentCart.cost.totalAmount, amount: '0' }
          }
        };
      }

      return { ...currentCart, ...updateCartTotals(updatedLines), lines: updatedLines };
    }

    case 'ADD_ITEM': {
      const { variant, product, quantity } = action.payload;
      const existingItem = currentCart.lines.find((item) => item.merchandise.id === variant.id);
      const updatedItem = createOrUpdateCartItem(existingItem, variant, product, quantity);

      const updatedLines = existingItem
        ? currentCart.lines.map((item) => (item.merchandise.id === variant.id ? updatedItem : item))
        : [...currentCart.lines, updatedItem];

      return {
        ...currentCart,
        ...updateCartTotals(updatedLines),
        lines: updatedLines
      };
    }

    case 'SET_LAST_ADDED': {
      // This action just passes through the state - the lastAddedItem is managed separately
      return currentCart;
    }

    case 'CLEAR_LAST_ADDED': {
      // This action just passes through the state - the lastAddedItem is managed separately
      return currentCart;
    }

    default:
      return currentCart;
  }
}

export function CartProvider({
  children,
  cartPromise
}: {
  children: React.ReactNode;
  cartPromise: Promise<Cart | undefined>;
}) {
  const initialCart = use(cartPromise);
  const [optimisticCart, updateOptimisticCart] = useOptimistic(initialCart, cartReducer);
  const [, startTransition] = useTransition();
  const lastAddedItemRef = useRef<string | null>(null);
  const previousCartRef = useRef(initialCart);
  
  // Cart modal state management
  const [isCartOpen, setIsCartOpen] = React.useState(false);
  
  const openCart = useCallback(() => {
    setIsCartOpen(true);
  }, []);
  
  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  // Sync optimistic state when the cart data changes (after revalidation)
  useEffect(() => {
    if (initialCart !== previousCartRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Cart data changed, syncing optimistic state', {
          previousItems: previousCartRef.current?.lines?.length || 0,
          newItems: initialCart?.lines?.length || 0
        });
      }
      
      // Reset optimistic state to match the new server data
      startTransition(() => {
        updateOptimisticCart({ type: 'RESET', payload: initialCart });
      });
      previousCartRef.current = initialCart;
    }
  }, [initialCart, updateOptimisticCart]);

  const updateCartItem = useCallback(
    (merchandiseId: string, updateType: UpdateType) => {
      // If we're decreasing or deleting, no inventory check needed
      if (updateType === 'minus' || updateType === 'delete') {
        updateOptimisticCart({ type: 'UPDATE_ITEM', payload: { merchandiseId, updateType } });
        return;
      }

      // When increasing, check inventory limits
      if (updateType === 'plus') {
        const existingItem = optimisticCart?.lines.find(
          (item) => item.merchandise.id === merchandiseId
        );
        if (!existingItem) {
          return; // Item not found, can't update
        }

        // Get the current variant from the cart item
        const variant = existingItem.merchandise;
        const currentQuantity = existingItem.quantity;

        // Create a simpler variant object with what we know
        const variantWithInventory = {
          id: variant.id,
          quantityAvailable: (variant as any).quantityAvailable,
          availableForSale: true,
          title: '',
          selectedOptions: [],
          price: { amount: '0', currencyCode: 'USD' }
        };

        // Use the shared validation utility to check if we can increment
        const { valid } = validateInventory(variantWithInventory, 1, currentQuantity);

        if (!valid) {
          console.warn(`Cannot increase quantity: at maximum stock level`);
          return; // Do not update if already at max
        }
      }

      // If inventory check passes or is not applicable, perform the update
      updateOptimisticCart({ type: 'UPDATE_ITEM', payload: { merchandiseId, updateType } });
    },
    [optimisticCart, updateOptimisticCart]
  );

  const addCartItem = useCallback(
    (variant: ProductVariant, product: Product, quantity?: number) => {
      // Check inventory limits before adding to cart
      const requestedQuantity = quantity || 1;

      // Get current quantity in cart for this variant
      const existingItem = optimisticCart?.lines.find((item) => item.merchandise.id === variant.id);
      const currentQuantity = existingItem?.quantity || 0;

      // Use the shared validation utility to check inventory
      const { valid, availableToAdd, message } = validateInventory(
        variant,
        requestedQuantity,
        currentQuantity
      );

      if (!valid) {
        console.warn(`Cannot add item: ${message}`);

        if (availableToAdd <= 0) {
          return; // Don't add anything if already at or exceeding max
        }

        // Add only what's available
        updateOptimisticCart({
          type: 'ADD_ITEM',
          payload: { variant, product, quantity: availableToAdd }
        });

        // Set this as the last added item
        lastAddedItemRef.current = variant.id;
        return;
      }

      // If inventory check passes, add normally
      updateOptimisticCart({
        type: 'ADD_ITEM',
        payload: { variant, product, quantity: requestedQuantity }
      });

      // Set this as the last added item
      lastAddedItemRef.current = variant.id;
    },
    [optimisticCart, updateOptimisticCart]
  );

  const clearLastAddedItem = useCallback(() => {
    lastAddedItemRef.current = null;
  }, []);

  const value = useMemo(
    () => ({
      cart: optimisticCart,
      updateCartItem,
      addCartItem,
      lastAddedItem: lastAddedItemRef.current,
      clearLastAddedItem,
      isCartOpen,
      openCart,
      closeCart
    }),
    [optimisticCart, updateCartItem, addCartItem, clearLastAddedItem, isCartOpen, openCart, closeCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
